import React from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/page-header';
import { OrgChartFlow } from '../components/org-chart';
import { useOrgChart } from '../hooks/useUsers';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const OrgChart: React.FC = () => {
  const { t } = useTranslation('common');
  const { data: orgChartData, isLoading, error, refetch } = useOrgChart();

  if (isLoading) {
    return (
      <div data-testid="org-chart-page">
        <PageHeader
          title={t('orgChart.title', 'Organizational Chart')}
          description={t('orgChart.description', 'View the company reporting hierarchy')}
          testId="org-chart-header"
        />
        <Card className="mt-6" data-testid="org-chart-loading">
          <CardContent className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-prosigliere-blue" />
              <p className="text-gray-500">{t('orgChart.loading', 'Loading organizational chart...')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !orgChartData) {
    return (
      <div data-testid="org-chart-page">
        <PageHeader
          title={t('orgChart.title', 'Organizational Chart')}
          description={t('orgChart.description', 'View the company reporting hierarchy')}
          testId="org-chart-header"
        />
        <Card className="mt-6" data-testid="org-chart-error">
          <CardContent className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="text-gray-700 font-medium">{t('orgChart.error', 'Failed to load organizational chart')}</p>
              <p className="text-gray-500 text-sm">{error.message}</p>
              <Button onClick={() => refetch()} variant="outline" data-testid="org-chart-button-retry">
                {t('common.retry', 'Try Again')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!orgChartData || orgChartData.length === 0) {
    return (
      <div data-testid="org-chart-page">
        <PageHeader
          title={t('orgChart.title', 'Organizational Chart')}
          description={t('orgChart.description', 'View the company reporting hierarchy')}
          testId="org-chart-header"
        />
        <Card className="mt-6" data-testid="org-chart-empty">
          <CardContent className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <p className="text-gray-500">{t('orgChart.empty', 'No organizational data available')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="org-chart-page">
      <PageHeader
        title={t('orgChart.title', 'Organizational Chart')}
        description={t('orgChart.description', 'View the company reporting hierarchy')}
        testId="org-chart-header"
      />
      <div className="mt-6" data-testid="org-chart-container">
        <OrgChartFlow data={orgChartData} />
      </div>
    </div>
  );
};

export default OrgChart;
